/**
 * The Space view's one line about the rest of the World (RFC 0336 3f, S10).
 *
 * Names the session asking you, since that is the one that needs you, and
 * counts the ones working. Off the registries alone, nothing opened, so it
 * can sit above the compact box all day. ⌘. (or a click) opens the lines.
 */

import type { WorldSessionRow } from '../../services/world/useWorldSessions'
import { lineCandidates } from './liveLines'
import { StatusDot } from './StatusDot'

export function worldSummary(
  rows: readonly WorldSessionRow[],
  opts: { worldAgentId: string | null; exclude: ReadonlySet<string> },
): { asking: WorldSessionRow[]; working: number } | null {
  const candidates = lineCandidates(rows, opts)
  // What asking means is the registry's rule (`registryStatus`), the same
  // one the lines read, so the two cannot disagree.
  const asking = candidates
    .filter((c) => c.state === 'asking')
    .map((c) => c.row)
  const working = candidates.filter((c) => c.state === 'working').length
  if (asking.length === 0 && working === 0) return null
  return { asking, working }
}

export function WorldSummary({
  summary,
  onOpen,
}: {
  summary: ReturnType<typeof worldSummary>
  onOpen: () => void
}) {
  if (!summary) return null
  const { asking, working } = summary
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full min-w-0 items-center gap-1.5 px-1 pb-1 text-left text-[12px] text-muted-foreground hover:text-foreground"
    >
      {asking.length > 0 ? (
        <span className="flex min-w-0 items-center gap-1.5">
          <StatusDot status="asking" />
          {asking.length === 1 ? (
            <>
              <span className="truncate font-medium text-foreground">
                {asking[0]?.title}
              </span>
              <span className="flex-none">is asking you</span>
            </>
          ) : (
            <span>{asking.length} asking you</span>
          )}
        </span>
      ) : null}
      {working > 0 ? (
        <span className="flex flex-none items-center gap-1.5">
          {asking.length > 0 ? <span aria-hidden>·</span> : null}
          <StatusDot status="working" />
          <span>{working} working</span>
        </span>
      ) : null}
      <span className="flex-1" />
      <span className="flex-none text-[11px]">⌘.</span>
    </button>
  )
}
